class ProductRecommendations extends HTMLElement {
  constructor() {
    super();

    var self = this;

    this._mutationObserver = new MutationObserver(function(mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var mutation = mutations[i];
        if (mutation.target !== self || mutation.type !== 'attributes') continue;
        if (mutation.attributeName === 'data-error') continue;
        if (mutation.attributeName === 'class' && self.classList.contains('hidden')) continue;
        if (
          mutation.attributeName === 'data-recommendations-performed' &&
          self.dataset.recommendationsPerformed === 'true'
        ) continue;
        self._loadRecommendations();
        break;
      }
    });

    this._cachedRecommendations = {};
    this._activeFetch = null;
  }

  connectedCallback() {
    this._initializeTabs();
    this._initializeViewMore();
    this._loadRecommendations();
    this._mutationObserver.observe(this, {
      attributes: true,
      attributeFilter: ['class', 'data-error', 'data-recommendations-performed', 'data-product-id', 'data-section-id', 'data-intent', 'data-url']
    });
  }

  disconnectedCallback() {
    this._mutationObserver.disconnect();
  }

  _loadRecommendations() {
    var productId = this.dataset.productId;
    var recommendationsPerformed = this.dataset.recommendationsPerformed;
    var sectionId = this.dataset.sectionId;
    var intent = this.dataset.intent;
    var id = this.id;
    var self = this;

    if (!productId || !id) {
      throw new Error('Product ID and an ID attribute are required');
    }

    if (recommendationsPerformed === 'true') {
      return;
    }

    this._fetchCachedRecommendations(productId, sectionId, intent)
      .then(function(result) {
        if (!result.success) {
          if (!Shopify.designMode) {
            self._handleError(new Error('Server returned ' + result.status));
          }
          return;
        }

        var html = document.createElement('div');
        html.innerHTML = result.data || '';
        self._preloadImages(html.querySelectorAll('img[src]'), false);
        var recommendations = html.querySelector('product-recommendations[id="' + id + '"]');
        var inner = recommendations ? recommendations.innerHTML : '';

        if (inner && inner.trim().length) {
          self.dataset.recommendationsPerformed = 'true';
          self.innerHTML = inner;
          self._initializeTabs();
          self._initializeViewMore();
        } else {
          self._handleError(new Error('No recommendations available'));
        }
      })
      .catch(function(e) {
        self._handleError(e);
      });
  }

  async _fetchCachedRecommendations(productId, sectionId, intent) {
    var url = this.dataset.url + '&product_id=' + productId + '&section_id=' + sectionId + '&intent=' + intent;

    var cachedResponse = this._cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    if (this._activeFetch) this._activeFetch.abort();
    this._activeFetch = new AbortController();

    try {
      var response = await fetch(url, { signal: this._activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      var text = await response.text();
      this._cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this._activeFetch = null;
    }
  }

  _preloadImages(imgs, checkComplete) {
    for (var i = 0; i < imgs.length; i++) {
      if (checkComplete && imgs[i].complete) continue;
      var temp = new Image();
      temp.src = imgs[i].src;
      if (imgs[i].srcset) temp.srcset = imgs[i].srcset;
    }
  }

  _handleError(error) {
    console.error('Product recommendations error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }

  _initializeTabs() {
    var tabsContainer = this.querySelector('[data-recommendations-tabs]');
    if (!(tabsContainer instanceof HTMLElement)) return;
    if (tabsContainer.dataset.tabsBound === 'true') return;

    var tabButtons = Array.from(tabsContainer.querySelectorAll('[data-recommendations-tab]')).filter(
      function(button) { return button instanceof HTMLButtonElement; }
    );
    var switchButton = tabsContainer.querySelector('[data-recommendations-switch]');

    if (tabButtons.length < 2) return;

    var selectedButton = tabButtons.find(function(button) { return button.classList.contains('is-active'); }) || tabButtons[0];
    var self = this;

    this._activateRecommendationTab(selectedButton.dataset.tabTarget, tabButtons, switchButton);

    for (var i = 0; i < tabButtons.length; i++) {
      (function(tabButton) {
        tabButton.addEventListener('click', function() {
          self._activateRecommendationTab(tabButton.dataset.tabTarget, tabButtons, switchButton);
        });
      })(tabButtons[i]);
    }

    if (switchButton instanceof HTMLButtonElement) {
      switchButton.addEventListener('click', function() {
        var activeButton = tabButtons.find(function(button) { return button.classList.contains('is-active'); });
        var activeTarget = activeButton ? activeButton.dataset.tabTarget : null;
        var nextTarget = activeTarget === 'from-drop' ? 'recommended' : 'from-drop';
        self._activateRecommendationTab(nextTarget, tabButtons, switchButton);
      });
    }

    tabsContainer.dataset.tabsBound = 'true';
  }

  _activateRecommendationTab(target, tabButtons, switchButton) {
    if (!target) return;

    for (var i = 0; i < tabButtons.length; i++) {
      var isActive = tabButtons[i].dataset.tabTarget === target;
      tabButtons[i].classList.toggle('is-active', isActive);
      tabButtons[i].setAttribute('aria-pressed', isActive ? 'true' : 'false');
    }

    if (switchButton instanceof HTMLButtonElement) {
      var isFromDrop = target === 'from-drop';
      switchButton.classList.toggle('is-from-drop', isFromDrop);
      switchButton.setAttribute('aria-checked', isFromDrop ? 'true' : 'false');
    }

    var recommendationPanels = this.querySelectorAll('[data-recommendations-panel]');
    for (var j = 0; j < recommendationPanels.length; j++) {
      var panel = recommendationPanels[j];
      if (!(panel instanceof HTMLElement)) continue;
      var revealing = panel.hidden && panel.dataset.recommendationsPanel === target;
      panel.hidden = panel.dataset.recommendationsPanel !== target;
      if (!revealing) continue;
      this._preloadImages(panel.querySelectorAll('img[src]'), true);
    }
  }

  _eagerLoadVisibleImages(root) {
    var imgs = root.querySelectorAll('img[loading="lazy"]');
    for (var i = 0; i < imgs.length; i++) {
      if (!imgs[i].closest('[hidden]')) imgs[i].loading = 'eager';
    }
  }

  _initializeViewMore() {
    var buttons = this.querySelectorAll('[data-view-more-button]');
    for (var i = 0; i < buttons.length; i++) {
      if (!(buttons[i] instanceof HTMLButtonElement)) continue;
      this._setupViewMoreButton(buttons[i]);
    }
  }

  _setupViewMoreButton(button) {
    if (button.dataset.viewMoreBound === 'true') return;

    var targetId = button.dataset.viewMoreTarget;
    if (!targetId) return;

    var grid = this.querySelector('#' + targetId);
    if (!(grid instanceof HTMLElement)) return;

    var items = Array.from(grid.querySelectorAll('[data-recommendation-item]'));
    if (!items.length) return;

    var configuredStep = Number.parseInt(button.dataset.viewMoreStep || '', 10);
    var fallbackStep = Number.parseInt(grid.dataset.viewMoreStep || '', 10);
    var step = Number.isFinite(configuredStep)
      ? configuredStep
      : Number.isFinite(fallbackStep)
        ? fallbackStep
        : items.length;

    if (step <= 0 || items.length <= step) {
      button.hidden = true;
      return;
    }

    var viewMoreText = button.dataset.viewMoreText || (button.textContent && button.textContent.trim()) || 'View more';
    var viewLessText = button.dataset.viewLessText || 'View less';
    var isExpanded = false;
    var visibleCount = step;
    var self = this;

    var updateVisibility = function() {
      for (var i = 0; i < items.length; i++) {
        var revealing = items[i].hidden && i < visibleCount;
        items[i].hidden = i >= visibleCount;
        if (revealing) self._eagerLoadVisibleImages(items[i]);
      }
      button.hidden = false;
      button.textContent = isExpanded ? viewLessText : viewMoreText;
    };

    updateVisibility();
    button.dataset.viewMoreBound = 'true';
    button.addEventListener('click', function() {
      visibleCount = isExpanded ? step : items.length;
      isExpanded = !isExpanded;
      updateVisibility();
    });
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
